import { UPLOAD_LOOKUP_SEPARATOR, type UploadLookup } from '@erp/rpc';

import {
  cellText,
  isBlankRow,
  type ResultsCell,
  type ResultsSheet,
} from './sheetCells';

/**
 * The second workbook some suppliers send (e.g. Stelpro's Feedback): its rows
 * keyed for matching, and how a layout names its columns (`Label: header`).
 */

/** Header → first column index with that (trimmed) header. */
export function headerIndex(headers: string[]): Map<string, number> {
  const index = new Map<string, number>();
  headers.forEach((header, column) => {
    const name = header.trim();
    if (!index.has(name)) index.set(name, column);
  });
  return index;
}

export function lookupPrefix(lookup: UploadLookup): string {
  return `${lookup.label}${UPLOAD_LOOKUP_SEPARATOR}`;
}

/** Splits the headers a layout reads into results-file and lookup-file ones. */
export function splitLayoutHeaders(
  headers: string[],
  lookup: UploadLookup | null,
): { results: string[]; lookup: string[] } {
  if (!lookup) return { results: headers, lookup: [] };
  const prefix = lookupPrefix(lookup);
  const results = new Set<string>();
  const fromLookup = new Set<string>();
  for (const header of headers) {
    if (header.startsWith(prefix)) fromLookup.add(header.slice(prefix.length));
    else results.add(header);
  }
  results.add(lookup.matchColumn);
  fromLookup.add(lookup.keyColumn);
  return { results: [...results], lookup: [...fromLookup] };
}

export interface LookupTable {
  prefix: string;
  matchColumn: string;
  columns: Map<string, number>;
  /** Key text → the first lookup row with that key. */
  rows: Map<string, ResultsCell[]>;
  /** Keys on more than one lookup row: which row applies is ambiguous. */
  duplicateKeys: Set<string>;
}

export function buildLookupTable(
  sheet: ResultsSheet,
  lookup: UploadLookup,
): LookupTable {
  const columns = headerIndex(sheet.headers);
  const keyColumn = columns.get(lookup.keyColumn);
  const rows = new Map<string, ResultsCell[]>();
  const duplicateKeys = new Set<string>();
  for (const cells of sheet.rows) {
    if (isBlankRow(cells) || keyColumn === undefined) continue;
    const key = cellText(cells[keyColumn]);
    if (key === '') continue;
    if (rows.has(key)) duplicateKeys.add(key);
    else rows.set(key, cells);
  }
  return {
    prefix: lookupPrefix(lookup),
    matchColumn: lookup.matchColumn,
    columns,
    rows,
    duplicateKeys,
  };
}
